import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import type { SettingType } from '@/types/app';

interface DetailCardProps {
	children: ReactNode;
	dataAttribute?: string;
	type: SettingType;
	isVisible?: boolean;
	title?: string;
	onClose?: () => void;
}

const DetailCard = ({
	children,
	dataAttribute,
	type,
	isVisible = true,
	onClose
}: DetailCardProps) => {
	const panelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isVisible) return;
		// preventScroll keeps programmatic focus from undoing scrollTo({ top: 0 }) when a
		// panel opens from quote selection (see quote-settings.cy.js scroll-to-top test).
		panelRef.current?.focus({ preventScroll: true });
	}, [isVisible]);

	useEffect(() => {
		if (!isVisible || !onClose) return;

		const handleKeyDown = (event: KeyboardEvent): void => {
			if (event.key !== 'Escape') return;
			const target = event.target;
			if (target instanceof HTMLElement && target.closest('[role="dialog"]')) return;

			event.preventDefault();
			onClose();
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isVisible, onClose]);

	if (!isVisible) return null;

	const dataProps: Record<string, string> = {};
	if (dataAttribute) {
		// Ensure the attribute has 'data-' prefix for proper data attribute
		const attrName = dataAttribute.startsWith('data-') ? dataAttribute : `data-${dataAttribute}`;
		dataProps[attrName] = '';
	}

	return (
		<div
			ref={panelRef}
			tabIndex={-1}
			{...dataProps}
			className={cn(
				'bg-white absolute shadow-lg select-none z-30',
				'fixed inset-x-0 bottom-0 top-16 w-full overscroll-contain',
				'md:absolute md:inset-auto md:h-auto md:top-auto md:overscroll-auto',
				'md:border md:border-border md:rounded',
				'md:w-full md:left-0',
				type === 'employees' && 'md:w-full md:left-[0%] 2xl:w-full 2xl:left-[0%]',
				type === 'absence' && 'md:w-[60%] md:left-[40%] 2xl:w-[43%] 2xl:left-[40%]',
				type === 'coverage' && 'md:w-full md:left-[0%] 2xl:w-[77%] 2xl:left-[23%]'
			)}
			data-testid={`settings-detail-card-${dataAttribute}`}
		>
			<div
				className={cn(
					'h-full overflow-y-auto overscroll-contain p-6 pl-5 sm:p-6 md:p-6 md:rounded md:h-auto md:overflow-visible md:overscroll-auto'
				)}
				style={{
					WebkitOverflowScrolling: 'touch'
				}}
			>
				<div className="md:px-1 md:pb-1 relative">
					{children}
					{/* Mobile-only close button (hidden on md and above) */}
					{onClose && (
						<button
							type="button"
							onClick={onClose}
							className="absolute -top-1 -right-3 rounded-sm opacity-70 hover:opacity-100 transition-opacity md:hidden"
							data-testid="detail-card-close-button"
							aria-label="Close"
						>
							<X className="h-5 w-5" />
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

export default DetailCard;
